/**
 * A tabela da resposta do agente (`W-14`, ADR-049): a grafia das células, a ordem e a
 * planilha.
 *
 * As mesmas regras de `plataforma/tabelas.py`: o valor chega cru (contagem, centavos,
 * percentual em pontos, data ISO) e é escrito aqui como a ficha o escreve — "1.234",
 * "R$ 1.234,56", "98,7%", "01/09/2026", "set/2026". A planilha é a que o Excel em português
 * abre sem perguntar nada: `;` entre colunas, vírgula decimal, BOM de UTF-8 — e nenhum texto
 * que comece como fórmula (a injeção de CSV: um nome "=HYPERLINK(…)" viraria link na
 * planilha de quem a abre).
 */
import type { ColunaDaTabela, TabelaDaFicha } from "../api/tipos";
import { numeroLongo, percentual, reais } from "../formato";

export type Celula = string | number | null;
type Tipo = ColunaDaTabela["tipo"];

const MESES = [
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez",
];

function dataIso(valor: Celula): [string, string, string] | null {
  const achado = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(valor));
  return achado ? [achado[1]!, achado[2]!, achado[3]!] : null;
}

export function formatarCelula(valor: Celula, tipo: Tipo): string {
  if (valor === null || valor === undefined) return "—";
  if (typeof valor === "number") {
    if (tipo === "contagem") return numeroLongo(Math.round(valor));
    if (tipo === "reais") return reais(Math.round(valor));
    if (tipo === "percentual") return percentual(valor);
  }
  if (tipo === "data" || tipo === "mes") {
    const partes = dataIso(valor);
    if (!partes) return String(valor);
    const [ano, mes, dia] = partes;
    return tipo === "data"
      ? `${dia}/${mes}/${ano}`
      : `${MESES[Number(mes) - 1]}/${ano}`;
  }
  return String(valor);
}

export function eNumerica(tipo: Tipo): boolean {
  return tipo === "contagem" || tipo === "reais" || tipo === "percentual";
}

export type Direcao = "asc" | "desc";

/** As linhas na ordem pedida; `null` é a ordem em que vieram (a natural: hora, dia, mês).
 *  O vazio vai sempre para o fim. O texto compara como gente: "2h" antes de "10h". */
export function ordenarLinhas(
  linhas: Celula[][],
  coluna: number | null,
  direcao: Direcao,
): Celula[][] {
  if (coluna === null) return linhas;
  const comparar = new Intl.Collator("pt-BR", { numeric: true }).compare;
  const sinal = direcao === "asc" ? 1 : -1;
  return [...linhas].sort((a, b) => {
    const x = a[coluna] ?? null;
    const y = b[coluna] ?? null;
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    if (typeof x === "number" && typeof y === "number") return (x - y) * sinal;
    return comparar(String(x), String(y)) * sinal;
  });
}

const COMECO_DE_FORMULA = /^[=+\-@\t\r]/;

function textoSeguro(texto: string): string {
  return COMECO_DE_FORMULA.test(texto) ? `'${texto}` : texto;
}

function celulaDoCsv(valor: Celula, tipo: Tipo): string {
  if (valor === null || valor === undefined) return "";
  if (typeof valor === "number" && eNumerica(tipo)) {
    if (tipo === "contagem") return String(Math.round(valor));
    if (tipo === "reais")
      return (Math.round(valor) / 100).toFixed(2).replace(".", ",");
    return valor.toFixed(1).replace(".", ",");
  }
  if (tipo === "data" || tipo === "mes") {
    const partes = dataIso(valor);
    if (!partes) return textoSeguro(String(valor));
    const [ano, mes, dia] = partes;
    return tipo === "data" ? `${dia}/${mes}/${ano}` : `${mes}/${ano}`;
  }
  return textoSeguro(String(valor));
}

function entreAspas(texto: string): string {
  return /[;"\n\r]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
}

function rotuloDoCsv(coluna: ColunaDaTabela): string {
  const sufixo =
    coluna.tipo === "reais"
      ? " (R$)"
      : coluna.tipo === "percentual"
        ? " (%)"
        : "";
  return textoSeguro(coluna.rotulo + sufixo);
}

/** A tabela inteira em CSV — a mesma de `plataforma.em_csv` e do WhatsApp. */
export function emCsv(tabela: TabelaDaFicha): string {
  const tipos = tabela.colunas.map((c) => c.tipo);
  const linhas = [
    tabela.colunas.map(rotuloDoCsv),
    ...tabela.linhas.map((linha) =>
      linha.map((v, i) => celulaDoCsv(v, tipos[i]!)),
    ),
  ];
  return (
    "\ufeff" + linhas.map((l) => l.map(entreAspas).join(";") + "\r\n").join("")
  );
}

/** "Passagens por hora do dia — últimos 7 dias" vira "passagens-por-hora-do-dia-ultimos-7-dias.csv". */
export function nomeDoArquivo(titulo: string): string {
  const base = titulo
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return `${base || "cockpit"}.csv`;
}

export function baixarCsv(tabela: TabelaDaFicha, titulo: string): void {
  const blob = new Blob([emCsv(tabela)], { type: "text/csv;charset=utf-8" });
  const endereco = URL.createObjectURL(blob);
  const ancora = document.createElement("a");
  ancora.href = endereco;
  ancora.download = nomeDoArquivo(titulo);
  document.body.appendChild(ancora);
  ancora.click();
  ancora.remove();
  setTimeout(() => URL.revokeObjectURL(endereco), 1000);
}
